import React, { useState } from 'react';
import { Plus, Trash2, MapPin, ChevronDown, ChevronUp, Home } from 'lucide-react';
import { Card, Button, Input } from '../../components/ui/index.jsx';
import { useGet } from '../../hooks/useApi';
import api from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';


const RegionList = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const { data: regionsResponse, refresh, loading } = useGet(`/regions?page=${currentPage}`);

    const regions = regionsResponse?.data || [];
    const pagination = regionsResponse?.meta || null;
    const { hasPermission } = useAuth();
    const canCreate = hasPermission('regions', 'create');
    const canDelete = hasPermission('regions', 'delete');
    const { success, error: toastError } = useToast();


    // Form state
    const [name, setName] = useState('');
    const [code, setCode] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Places state
    const [expandedRegion, setExpandedRegion] = useState(null);
    const [newPlaceName, setNewPlaceName] = useState('');
    const [isAddingPlace, setIsAddingPlace] = useState(false);
    const [isDeletingPlace, setIsDeletingPlace] = useState(null);

    const handleAdd = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            await api.post('/regions', { region_name: name, code });
            success('تم إضافة المنطقة بنجاح');
            setName('');
            setCode('');
            refresh();
        } catch (err) {
            toastError('خطأ أثناء الإضافة');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async (id) => {
        if (confirm('هل أنت متأكد من حذف هذه المنطقة؟')) {
            try {
                await api.delete(`/regions/${id}`);
                success('تم حذف المنطقة بنجاح');
                if (expandedRegion === id) setExpandedRegion(null);
                refresh();
            } catch (err) {
                toastError('خطأ أثناء الحذف');
            }
        }
    };

    const toggleExpand = (regionId) => {
        setExpandedRegion(prev => prev === regionId ? null : regionId);
        setNewPlaceName('');
    };

    const handleAddPlace = async (regionId) => {
        if (!newPlaceName.trim()) return;
        setIsAddingPlace(true);
        try {
            await api.post(`/regions/${regionId}/places`, { place_name: newPlaceName.trim() });
            success('تم إضافة المكان بنجاح');
            setNewPlaceName('');
            refresh();
        } catch (err) {
            toastError('خطأ أثناء إضافة المكان');
        } finally {
            setIsAddingPlace(false);
        }
    };

    const handleDeletePlace = async (regionId, placeId) => {
        if (!confirm('هل أنت متأكد من حذف هذا المكان؟')) return;
        setIsDeletingPlace(placeId);
        try {
            await api.delete(`/regions/${regionId}/places/${placeId}`);
            success('تم حذف المكان بنجاح');
            refresh();
        } catch (err) {
            toastError('خطأ أثناء حذف المكان');
        } finally {
            setIsDeletingPlace(null);
        }
    };

    return (
        <div className="space-y-8 animate-in fade-in duration-500">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                <div>
                    <h2 className="text-xl  text-slate-800">إدارة المناطق</h2>
                    <p className="text-slate-400 text-[11px] leading-none mt-1 ">تكوين وتنظيم مناطق التوصل المدرسي</p>
                </div>
                <div className="flex bg-white px-4 py-2 rounded-lg border border-slate-100 items-center gap-2">
                    <MapPin className="text-primary w-5 h-5" />
                    <span className="text-sm  text-slate-600">إجمالي المناطق: {pagination?.total || 0}</span>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Form Section */}
                {canCreate ? (
                    <Card className="p-4 h-fit lg:col-span-1 border-primary/10 bg-orange-50/10">
                        <h3 className="text-lg  text-slate-800 mb-6 flex items-center gap-2">
                            <Plus className="w-5 h-5 text-primary" /> إضافة منطقة جديدة
                        </h3>
                        <form onSubmit={handleAdd} className="space-y-4">
                            <Input
                                label="كود المنطقة"
                                value={code}
                                onChange={e => setCode(e.target.value)}
                                placeholder="مثال: REG-01"
                                required
                            />
                            <Input
                                label="اسم المنطقة"
                                value={name}
                                onChange={e => setName(e.target.value)}
                                placeholder="مثال: الحي الحسني"
                                required
                            />
                            <Button type="submit" isLoading={isSubmitting} className="w-full mt-2">
                                حفظ المنطقة
                            </Button>
                        </form>
                    </Card>
                ) : (
                    <Card className="p-8 h-fit lg:col-span-1 border-slate-100 bg-slate-50/50 text-center flex flex-col items-center justify-center">
                        <MapPin className="w-8 h-8 text-slate-300 mb-2" />
                        <p className="text-xs text-slate-400">لا تملك صلاحية إضافة مناطق جديدة</p>
                    </Card>
                )}

                {/* List Section */}
                <Card className="lg:col-span-2 overflow-hidden border-slate-100">
                    <div className="overflow-x-auto">
                        <table className="w-full text-right">
                            <thead className="bg-slate-50 border-b border-slate-100">
                                <tr>
                                    <th className="px-6 py-2.5 text-[10px]  text-slate-400 uppercase tracking-widest">الكود</th>
                                    <th className="px-6 py-2.5 text-[10px]  text-slate-400 uppercase tracking-widest">اسم المنطقة</th>
                                    <th className="px-6 py-2.5 text-[10px]  text-slate-400 uppercase tracking-widest">الأماكن</th>
                                    <th className="px-6 py-2.5 text-[10px]  text-slate-400 uppercase tracking-widest text-left">الإجراءات</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {loading && (
                                    <tr>
                                        <td colSpan="4" className="px-6 py-8 text-center text-slate-400 animate-pulse">جاري التحميل...</td>
                                    </tr>
                                )}
                                {!loading && regions?.length === 0 && (
                                    <tr>
                                        <td colSpan="4" className="px-6 py-8 text-center text-slate-400">لا توجد مناطق مسجلة حالياً</td>
                                    </tr>
                                )}
                                {regions?.map(region => (
                                    <React.Fragment key={region.id}>
                                        <tr className="hover:bg-slate-50/50 transition-colors">
                                            <td className="px-6 py-4">
                                                <span className=" text-primary bg-primary/5 px-2 py-1 rounded text-sm">
                                                    #{region.code}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4  text-slate-800">{region.region_name}</td>
                                            <td className="px-6 py-4">
                                                <button
                                                    onClick={() => toggleExpand(region.id)}
                                                    className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-primary transition-colors group"
                                                >
                                                    <Home size={14} className="group-hover:text-primary" />
                                                    <span>{region.places?.length || 0} مكان</span>
                                                    {expandedRegion === region.id
                                                        ? <ChevronUp size={13} />
                                                        : <ChevronDown size={13} />
                                                    }
                                                </button>
                                            </td>
                                             <td className="px-6 py-4 text-left">
                                                {canDelete ? (
                                                    <button
                                                        onClick={() => handleDelete(region.id)}
                                                        className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                                                        title="حذف المنطقة"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                ) : (
                                                    <span className="text-[10px] text-slate-300">غير مسموح</span>
                                                )}
                                            </td>
                                        </tr>

                                        {/* Places expanded row */}
                                        {expandedRegion === region.id && (
                                            <tr>
                                                <td colSpan="4" className="bg-orange-50/20 border-b border-orange-100/50 px-6 py-4">
                                                    <div className="space-y-3">
                                                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                                                            <Home size={12} className="text-primary" />
                                                            أماكن منطقة {region.region_name}
                                                        </p>

                                                        {/* Existing places list */}
                                                        {region.places && region.places.length > 0 ? (
                                                            <div className="flex flex-wrap gap-2">
                                                                {region.places.map(place => (
                                                                    <div
                                                                        key={place.id}
                                                                        className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-sm text-slate-700 shadow-sm"
                                                                    >
                                                                         <Home size={12} className="text-primary/60" />
                                                                        <span>{place.place_name}</span>
                                                                        {canDelete && (
                                                                            <button
                                                                                onClick={() => handleDeletePlace(region.id, place.id)}
                                                                                disabled={isDeletingPlace === place.id}
                                                                                className="mr-1 text-slate-300 hover:text-red-500 transition-colors disabled:opacity-50"
                                                                                title="حذف المكان"
                                                                            >
                                                                                {isDeletingPlace === place.id
                                                                                    ? <span className="text-[10px]">...</span>
                                                                                    : <Trash2 size={13} />
                                                                                }
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <p className="text-xs text-slate-400 italic">لا توجد أماكن مسجلة لهذه المنطقة</p>
                                                        )}

                                                         {/* Add new place row */}
                                                        {canCreate && (
                                                            <div className="flex items-center gap-2 mt-2">
                                                                <input
                                                                    type="text"
                                                                    value={newPlaceName}
                                                                    onChange={e => setNewPlaceName(e.target.value)}
                                                                    onKeyDown={e => e.key === 'Enter' && handleAddPlace(region.id)}
                                                                    placeholder="اسم المكان الجديد..."
                                                                    className="flex-1 text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50 bg-white"
                                                                />
                                                                <button
                                                                    onClick={() => handleAddPlace(region.id)}
                                                                    disabled={isAddingPlace || !newPlaceName.trim()}
                                                                    className="flex items-center gap-1.5 bg-primary text-white text-sm px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                                >
                                                                    <Plus size={15} />
                                                                    {isAddingPlace ? 'جاري...' : 'إضافة مكان'}
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination Controls */}
                    {pagination && pagination.last_page > 1 && (
                        <div className="px-6 py-4 bg-slate-50/30 border-t border-slate-100 flex items-center justify-between">
                            <div className="text-xs text-slate-500 font-normal">
                                عرض <span className="font-medium text-slate-700">{pagination.from}</span> إلى <span className="font-medium text-slate-700">{pagination.to}</span> من أصل <span className="font-medium text-slate-700">{pagination.total}</span> منطقة
                            </div>
                            <div className="flex items-center gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                    disabled={currentPage === 1}
                                    className="h-8 px-3 text-xs"
                                >
                                    السابق
                                </Button>
                                <div className="flex items-center gap-1">
                                    {Array.from({ length: Math.min(5, pagination.last_page) }, (_, i) => {
                                        let pageNum = i + 1;
                                        if (pagination.last_page > 5 && currentPage > 3) {
                                            pageNum = currentPage - 3 + i + 1;
                                            if (pageNum > pagination.last_page) pageNum = pagination.last_page - (4 - i);
                                        }
                                        return (
                                            <button
                                                key={pageNum}
                                                onClick={() => setCurrentPage(pageNum)}
                                                className={`w-8 h-8 rounded-lg text-xs transition-all ${currentPage === pageNum
                                                    ? 'bg-primary text-white shadow-sm font-medium'
                                                    : 'text-slate-500 hover:bg-slate-100'
                                                    }`}
                                            >
                                                {pageNum}
                                            </button>
                                        );
                                    })}
                                </div>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, pagination.last_page))}
                                    disabled={currentPage === pagination.last_page}
                                    className="h-8 px-3 text-xs"
                                >
                                    التالي
                                </Button>
                            </div>
                        </div>
                    )}
                </Card>
            </div>
        </div>
    );
};

export default RegionList;
